/* SemBot · le chiamate, una funzione per endpoint.
 *
 * Ogni funzione porta accanto le insidie dell'endpoint che avvolge — quelle
 * scoperte leggendo i controller — perché sono la ragione per cui una
 * schermata funziona in sviluppo e crolla sui dati veri.
 */

import { api, normalizzaLista, queryCrud, type ParametriCrud } from './client';
import type {
  Account,
  AttivitaWallet,
  AvvioBacktest,
  AvvioGruppoBacktest,
  Backtest,
  BacktestGroup,
  Candela,
  Catena,
  CredenzialiAccesso,
  EsitoLiquidita,
  EsitoProvaRpc,
  Exchange,
  FaccetteBacktest,
  FiltriRicercaBacktest,
  IndirizzoDeposito,
  MarketWatcher,
  PaginaCrud,
  PersonalWallet,
  ProfiloUtente,
  RangeDatiStorici,
  RiepilogoSaldoWallet,
  RispostaAccesso,
  RisultatoRicercaBacktest,
  SaldiMulticatena,
  SaldiTuttiAccount,
  SaldiWalletSuCatena,
  SaldoAccount,
  StatoSchemaDatiStorici,
  StrategiaDisponibile,
  Token,
  Tradebot,
  Wallet,
} from './tipi';

/* ==========================================================================
 * AUTENTICAZIONE
 * ======================================================================== */

export const auth = {
  /** L'unico endpoint pubblico del backend. */
  accedi: (credenziali: CredenzialiAccesso) =>
    api.post<RispostaAccesso>('/auth/login', credenziali),

  /** Non restituisce l'id utente: quello arriva solo dalla risposta di login. */
  profilo: () => api.get<ProfiloUtente>('/auth/profile'),
};

/* ==========================================================================
 * ACCOUNT ED EXCHANGE
 * ======================================================================== */

export const accounts = {
  elenco: (p?: ParametriCrud) =>
    api
      .get<Account[] | PaginaCrud<Account>>(`/accounts${queryCrud(p)}`)
      .then(normalizzaLista),

  uno: (id: number) => api.get<Account>(`/accounts/${id}`),

  /** `apiKey` e `apiSecret` si inviano solo alla creazione: il CRUD li esclude
   *  dalle risposte, quindi non tornano mai indietro. */
  crea: (dati: Partial<Account> & { apiKey: string; apiSecret: string; apiPassphrase?: string }) =>
    api.post<Account>('/accounts', dati),

  aggiorna: (id: number, dati: Partial<Account>) =>
    api.patch<Account>(`/accounts/${id}`, dati),

  elimina: (id: number) => api.del<void>(`/accounts/${id}`),

  /** Lenta e seriale: N account × fetchBalance + fetchTickers, senza timeout
   *  complessivo. Va chiamata con uno stato di caricamento esplicito, mai in
   *  sottofondo mentre l'utente sta per premere qualcosa. Cache server 300 s. */
  saldiTutti: (segnale?: AbortSignal) =>
    api.get<SaldiTuttiAccount>('/accounts/get-all-balances', undefined, segnale),

  saldo: (accountId: number, segnale?: AbortSignal) =>
    api.get<SaldoAccount>('/accounts/get-account-balance', { accountId }, segnale),

  /** Restituisce `null` — non un errore — quando l'exchange rifiuta. */
  indirizzoDeposito: (accountId: number, currency: string) =>
    api.get<IndirizzoDeposito | null>('/accounts/get-deposit-address', {
      accountId,
      currency,
    }),
};

export const exchanges = {
  elenco: (p?: ParametriCrud) =>
    api
      .get<Exchange[] | PaginaCrud<Exchange>>(`/exchanges${queryCrud(p)}`)
      .then(normalizzaLista),

  uno: (id: number) => api.get<Exchange>(`/exchanges/${id}`),
  crea: (dati: Pick<Exchange, 'name' | 'slug'> & Partial<Exchange>) =>
    api.post<Exchange>('/exchanges', dati),
  aggiorna: (id: number, dati: Partial<Exchange>) =>
    api.patch<Exchange>(`/exchanges/${id}`, dati),
  elimina: (id: number) => api.del<void>(`/exchanges/${id}`),
};

export const marketWatchers = {
  elenco: (p?: ParametriCrud) =>
    api
      .get<MarketWatcher[] | PaginaCrud<MarketWatcher>>(`/market-watchers${queryCrud(p)}`)
      .then(normalizzaLista),

  uno: (id: number) => api.get<MarketWatcher>(`/market-watchers/${id}`),
  crea: (dati: Pick<MarketWatcher, 'asset' | 'currency' | 'exchangeId'> & Partial<MarketWatcher>) =>
    api.post<MarketWatcher>('/market-watchers', dati),
  aggiorna: (id: number, dati: Partial<MarketWatcher>) =>
    api.patch<MarketWatcher>(`/market-watchers/${id}`, dati),
  elimina: (id: number) => api.del<void>(`/market-watchers/${id}`),
};

/* ==========================================================================
 * TRADEBOT
 * ======================================================================== */

export const tradebots = {
  /**
   * `trades` è una relazione EAGER: senza `fields` la lista scarica tutti i
   * trade di tutti i bot. Il valore predefinito qui chiede solo le colonne che
   * servono a una riga di lista — è l'unico modo di tenere la pagina viva
   * quando i bot hanno anni di storia.
   */
  elenco: (p?: ParametriCrud) =>
    api
      .get<Tradebot[] | PaginaCrud<Tradebot>>(`/tradebots${queryCrud(p ?? CAMPI_LISTA_BOT)}`)
      .then(normalizzaLista),

  /** Qui i trade servono davvero, quindi si prende la riga intera. */
  uno: (id: number) => api.get<Tradebot>(`/tradebots/${id}`),

  crea: (dati: Partial<Tradebot>) => api.post<Tradebot>('/tradebots', dati),

  aggiorna: (id: number, dati: Partial<Tradebot>) =>
    api.patch<Tradebot>(`/tradebots/${id}`, dati),

  elimina: (id: number) => api.del<void>(`/tradebots/${id}`),

  /**
   * Non esiste `POST /tradebots/:id/stop`: il ciclo di vita si pilota
   * scrivendo `isRunning`, ed è il processo esterno a reagirci. La conferma
   * non è immediata, quindi la schermata deve mostrare "in arresto" e non
   * "fermo" finché il bot non lo conferma da solo.
   */
  ferma: (id: number) => api.patch<Tradebot>(`/tradebots/${id}`, { isRunning: false }),
  avvia: (id: number) => api.patch<Tradebot>(`/tradebots/${id}`, { isRunning: true }),

  /** Duplicato di `GET /market-watchers`. La rotta è `getRepository(MarketWatcher).find()` e
   *  `exchange` è `eager: true` sull'entity: la relazione ARRIVA. Il commento diceva il
   *  contrario e portava a scrivere ripieghi per un dato che c'era già. */
  marketWatchers: () => api.get<MarketWatcher[]>('/tradebots/market-watchers'),
};

/** Colonne minime per una riga di lista bot, senza i trade eager. */
export const CAMPI_LISTA_BOT: ParametriCrud = {
  fields: [
    'id',
    'isRunning',
    'useRealMoney',
    'asset',
    'currency',
    'strategyName',
    'candleSize',
    'accountId',
    'walletId',
    'chainId',
    'dex',
    'marketChange',
    'strategyChange',
    'finalTotalBalance',
    'initialValue',
    'startDate',
    'createdAt',
    'updatedAt',
    /* Il battito e il prezzo che il bot vede: senza questi la lista non puo' distinguere un bot
       vivo che sta fermo in posizione da uno il cui processo e' morto, e non puo' valutare il
       capitale di chi ha tutto nell'asset. Costano tre colonne su una riga che ne porta gia'
       diciotto. */
    'lastSeenAt',
    'lastSeenPrice',
    'measuredSince',
    'assetBalance',
    'currencyBalance',
    'parkedCurrency',
    'initialAssetPrice',
    'finalAssetPrice',
    'failedTransactions',
    'errors',
  ],
  sort: ['id,DESC'],
};

/* ==========================================================================
 * WALLET
 * ======================================================================== */

export const wallets = {
  /** La risposta contiene `encryptedPrivateKey`: non mostrarlo e non loggarlo. */
  elenco: (p?: ParametriCrud) =>
    api
      .get<Wallet[] | PaginaCrud<Wallet>>(`/wallets${queryCrud(p)}`)
      .then(normalizzaLista),

  uno: (id: number) => api.get<Wallet>(`/wallets/${id}`),

  /** In `encryptedPrivateKey` si passa la chiave IN CHIARO: il server la cifra
   *  in `@BeforeInsert`. Il campo non va mai riletto né rimandato indietro. */
  crea: (dati: { name: string; encryptedPrivateKey: string }) =>
    api.post<Wallet>('/wallets', dati),

  aggiorna: (id: number, dati: Partial<Pick<Wallet, 'name'>>) =>
    api.patch<Wallet>(`/wallets/${id}`, dati),

  elimina: (id: number) => api.del<void>(`/wallets/${id}`),

  /** Massimo 25 id per richiesta: oltre, il server risponde 400. */
  riepilogoSaldi: (
    opzioni: { ids?: number[]; cacheOnly?: boolean; refresh?: boolean; limit?: number } = {},
    segnale?: AbortSignal,
  ) =>
    api.get<RiepilogoSaldoWallet[]>(
      '/wallets/balances/summary',
      {
        ids: opzioni.ids,
        cacheOnly: opzioni.cacheOnly ? '1' : undefined,
        refresh: opzioni.refresh ? '1' : undefined,
        limit: opzioni.limit,
      },
      segnale,
    ),

  /** `chainId` è obbligatorio: senza, il server risponde 400. */
  saldiSuCatena: (
    id: number,
    chainId: number,
    opzioni: { include?: string[]; limit?: number; rpc?: string } = {},
    segnale?: AbortSignal,
  ) => api.get<SaldiWalletSuCatena>(`/wallets/${id}/balances`, { chainId, ...opzioni }, segnale),

  /** Cache server ~45 s. Una catena che fallisce torna `ok:false`, non rompe. */
  saldiTutteLeCatene: (
    id: number,
    opzioni: { limit?: number; refresh?: boolean } = {},
    segnale?: AbortSignal,
  ) =>
    api.get<SaldiMulticatena>(
      `/wallets/${id}/balances/all`,
      { limit: opzioni.limit, refresh: opzioni.refresh ? '1' : undefined },
      segnale,
    ),

  /** `partial: true` significa elenco incompleto per costruzione: va detto a
   *  video, non nascosto, altrimenti si legge come "non ci sono transazioni". */
  transazioni: (
    id: number,
    chainId: number,
    opzioni: { blocks?: number; limit?: number; tokens?: string[]; rpc?: string } = {},
    segnale?: AbortSignal,
  ) => api.get<AttivitaWallet>(`/wallets/${id}/transactions`, { chainId, ...opzioni }, segnale),
};

/**
 * La cassaforte personale. Da NON confondere con `wallets`, qui sopra: quelli
 * sono i wallet operativi dei tradebot, cifrati dal server con la sua
 * SECURITY_KEY. Questi non li legge nessun bot.
 *
 * `address`, `privateKey` e `mnemonic` arrivano e partono **gia' cifrati dal
 * browser**: l'entity non ha alcun hook di cifratura, il server scrive la
 * stringa che riceve. La password non passa di qui e non esiste da nessuna
 * parte fuori dalla testa di chi l'ha scelta. Il modulo che cifra e decifra e'
 * `src/pagine/cassaforte/cifratura.ts`.
 *
 * Il CRUD non ha `@Exclude()` sui tre campi: `elenco` va quindi chiamato con
 * `CAMPI_ELENCO` (`pagine/cassaforte/dati.ts`), che li lascia fuori. `uno` li
 * porta tutti, ed e' voluto: si chiama solo quando si apre un wallet.
 */
export const personalWallets = {
  elenco: (p?: ParametriCrud) =>
    api
      .get<PersonalWallet[] | PaginaCrud<PersonalWallet>>(`/personal-wallets${queryCrud(p)}`)
      .then(normalizzaLista),
  uno: (id: number) => api.get<PersonalWallet>(`/personal-wallets/${id}`),
  /* I tre valori sono testo cifrato, non segreti in chiaro: il tipo li vuole
     stringhe e basta, ma il nome del parametro lo dice a chi chiama. */
  /* `mnemonic` è facoltativa e la colonna è nullable: un wallet importato da
     una chiave privata non ha una frase, e inventarne una vuota renderebbe
     indistinguibile «non ce l'ha» da «non si è salvata». */
  crea: (dati: { name: string; address: string; privateKey: string; mnemonic?: string }) =>
    api.post<PersonalWallet>('/personal-wallets', dati),
  elimina: (id: number) => api.del<void>(`/personal-wallets/${id}`),
};

/* ==========================================================================
 * BACKTEST
 * ======================================================================== */

export const backtest = {
  strategie: () => api.get<StrategiaDisponibile[]>('/backtester/strategies'),

  /**
   * L'UNICO endpoint del backend con paginazione e ordinamento veri. Su
   * 1.507.455 righe l'ordinamento è una query, non un riordino di pagina: è
   * per questo che la lista si blocca mentre interroga.
   *
   * Attenzione alla semantica delle date: `startDate`/`endDate` selezionano i
   * backtest che COPRONO l'intervallo, non quelli contenuti in esso.
   */
  cerca: (filtri: FiltriRicercaBacktest = {}, segnale?: AbortSignal) =>
    api.get<RisultatoRicercaBacktest>(
      '/backtester/search',
      { ...filtri, limit: Math.min(filtri.limit ?? 50, 200) },
      segnale,
    ),

  /** Valori distinti per popolare i filtri. I conteggi `n` sono STRINGHE. */
  faccette: (segnale?: AbortSignal) =>
    api.get<FaccetteBacktest>('/backtester/search/facets', undefined, segnale),

  /** Entity completa, con le serie: pesa megabyte. Mai in lista. */
  uno: (id: number, segnale?: AbortSignal) =>
    api.get<Backtest | null>(`/backtester/${id}`, undefined, segnale),

  /**
   * Senza filtri torna le ultime 200 righe COMPLETE, serie incluse. Con
   * filtri non ha limite. Da preferire sempre `cerca`, tranne per i preferiti
   * dove serve la lista intera e non esiste alternativa.
   */
  elencoGrezzo: (
    opzioni: {
      backtestGroupId?: number;
      isFavorite?: boolean;
      single?: boolean;
    } = {},
    /* Il segnale c'è perché questa è la chiamata più pesante
       dell'applicazione: uscire dalla schermata mentre scarica megabyte di
       serie lasciava la richiesta viva fino in fondo. Tutte le altre chiamate
       pesanti lo prendevano già, e i Preferiti erano dovuti passare da
       `api.get` a mano per averlo. */
    segnale?: AbortSignal,
  ) =>
    api.get<Backtest[]>(
      '/backtester',
      {
        backtestGroupId: opzioni.backtestGroupId,
        isFavorite: opzioni.isFavorite == null ? undefined : String(opzioni.isFavorite),
        single: opzioni.single ? 'true' : undefined,
      },
      segnale,
    ),

  /** Senza `isFavorite` nel corpo il server inverte il valore corrente. */
  segnaPreferito: (id: number, isFavorite?: boolean) =>
    api.patch<Backtest | null>(`/backtester/${id}/favorite`, { isFavorite }),

  descrivi: (id: number, description: string | null) =>
    api.patch<Backtest | null>(`/backtester/${id}/description`, { description }),

  elimina: (id: number) => api.del<{ deleted: boolean }>(`/backtester/${id}`),

  /** Fire-and-forget: torna la riga con `status: 'RUNNING'` e va poi pollata
   *  su `uno(id)` finché `status` diventa DONE o ERROR. */
  avvia: (dati: AvvioBacktest) => api.post<Backtest>('/backtester/run', dati),

  avviaGruppo: (dati: AvvioGruppoBacktest) =>
    api.post<BacktestGroup>('/backtester/run-group', dati),

  /** Risponde con un booleano nudo, non con un oggetto. */
  forzaGruppo: (backtestGroupId: number) =>
    api.post<boolean>('/backtester/force-run-group', { backtestGroupId }),

  /** SINCRONO e potenzialmente lunghissimo: nessun timeout lato server. */
  ricalcolaMetriche: (id: number, segnale?: AbortSignal) =>
    api.post<Backtest | null>(`/backtester/${id}/recalculate-metrics`, undefined, segnale),
};

/** I gruppi stanno su un path al SINGOLARE, e non si creano da qui: la
 *  creazione passa solo da `backtest.avviaGruppo`. */
export const gruppiBacktest = {
  elenco: (p?: ParametriCrud) =>
    api
      .get<BacktestGroup[] | PaginaCrud<BacktestGroup>>(`/backtest-group${queryCrud(p)}`)
      .then(normalizzaLista),

  uno: (id: number) => api.get<BacktestGroup>(`/backtest-group/${id}`),

  aggiorna: (id: number, dati: Partial<BacktestGroup>) =>
    api.patch<BacktestGroup>(`/backtest-group/${id}`, dati),

  elimina: (id: number) => api.del<void>(`/backtest-group/${id}`),
};

/* ==========================================================================
 * DATI STORICI
 * ======================================================================== */

export const datiStorici = {
  /** Cache su disco, ricalcolata da un cron ogni 2 ore. Range non contigui
   *  dello stesso pair sono righe distinte: un buco è una riga in più. */
  schema: (segnale?: AbortSignal) =>
    api.get<RangeDatiStorici[]>('/historical-data/get-historical-data', undefined, segnale),

  statoSchema: () => api.get<StatoSchemaDatiStorici>('/historical-data/schema-status'),

  aggiornaSchema: () =>
    api.post<{ started: boolean; alreadyRunning?: boolean } & StatoSchemaDatiStorici>(
      '/historical-data/refresh-schema',
    ),

  /** SINCRONO: scarica candele da 1 minuto in loop, senza feedback di
   *  avanzamento. Una richiesta su anni di storia può durare molti minuti. */
  scarica: (dati: {
    exchangeId: number;
    asset: string;
    currency: string;
    startDate: string;
    endDate: string;
  }) => api.post<unknown>('/historical-data/fetch-data', dati),

  /** Anteprima fissa a 50 righe, senza ordinamento né filtro temporale. */
  anteprimaCandele: (exchangeId: number, asset: string, currency: string) =>
    api.get<Candela[]>('/historical-data/get-candles', { exchangeId, asset, currency }),

  /** DELETE con corpo: il server legge i parametri da `@Body`. Risponde con
   *  un numero nudo, le righe cancellate. */
  elimina: (dati: { exchangeId: number; asset: string; currency: string }) =>
    api.del<number>('/historical-data/delete', dati),
};

/* ==========================================================================
 * TOKEN E CATENE
 * ======================================================================== */

export const tokens = {
  /** Nessuna chiamata di rete: è la tabella delle 4 catene supportate, con i
   *  valori di gas da precompilare nel modulo del bot. */
  catene: () => api.get<Catena[]>('/tokens/chains'),

  /** `chainId` obbligatorio. `limit` è tagliato a 200 dal server. */
  cerca: (chainId: number, q?: string, limit?: number, segnale?: AbortSignal) =>
    api.get<Token[]>('/tokens', { chainId, q, limit }, segnale),

  /** Un RPC irraggiungibile NON è un errore HTTP: arriva `ok:false` con un
   *  `message` già in italiano da mostrare accanto al campo. */
  provaRpc: (url: string, chainId: number, segnale?: AbortSignal) =>
    api.post<EsitoProvaRpc>('/tokens/rpc-check', { url, chainId }, segnale),

  /** Con `depthCurve: true` costa fino a 7 preventivi extra: si attiva solo su
   *  un'azione esplicita, mai a ogni battuta di tasto in un campo. */
  liquidita: (
    dati: {
      chainId: number;
      sellToken: string;
      buyToken: string;
      amountUsd: number;
      marketWatcherId?: number;
      depthCurve?: boolean;
    },
    segnale?: AbortSignal,
  ) => api.post<EsitoLiquidita>('/tokens/liquidity-check', dati, segnale),
};
